import type { StageColor } from "@purlink/shared/leads-api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, Columns3, Heart, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "~/components/shell/app-shell";
import { Badge, StageDot } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState, Skeleton } from "~/components/ui/feedback";
import { Tooltip } from "~/components/ui/menu";
import { toast } from "~/components/ui/toaster";
import { leadKeys, stagesQuery, type Stage } from "~/features/leads/api";
import { DeleteStageDialog, StageFormDialog } from "~/features/stages/stage-dialogs";
import { api, unwrap } from "~/lib/api";
import { errorMessage, fieldErrorsOf } from "~/lib/auth";
import { useCan } from "~/lib/use-can";

import { cn } from "~/lib/utils";
export const Route = createFileRoute("/_app/settings/stages")({
  component: StagesPage,
});

function StagesPage() {
  const queryClient = useQueryClient();
  // Deleting a stage (and its leads) is Super Admin only.
  const canDelete = useCan()("stage", "delete");
  const stages = useQuery(stagesQuery);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Stage | null>(null);
  const [deleting, setDeleting] = useState<Stage | null>(null);
  const [formError, setFormError] = useState<string>();

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: stagesQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: leadKeys.all }),
      queryClient.invalidateQueries({ queryKey: ["pipeline"] }),
    ]);

  const create = useMutation({
    mutationFn: (values: { name: string; color: StageColor }) =>
      unwrap(api.api.stages.$post({ json: values })),
    onSuccess: async () => {
      await refresh();
      setCreating(false);
      toast.success("Stage added.");
    },
    onError: (error) => setFormError(fieldErrorsOf(error).name ?? errorMessage(error)),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      ...json
    }: {
      id: string;
      name?: string;
      color?: StageColor;
      isDefault?: true;
    }) => unwrap(api.api.stages[":id"].$patch({ param: { id }, json })),
    onSuccess: async (_data, variables) => {
      await refresh();
      setEditing(null);
      toast.success(
        variables.isDefault ? "New leads will now arrive in this stage." : "Stage saved.",
      );
    },
    onError: (error) => setFormError(fieldErrorsOf(error).name ?? errorMessage(error)),
  });
  const move = useMutation({
    mutationFn: ({
      id,
      beforeId,
      afterId,
    }: {
      id: string;
      beforeId: string | null;
      afterId: string | null;
    }) => unwrap(api.api.stages[":id"].move.$post({ param: { id }, json: { beforeId, afterId } })),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: ({ id, newDefaultStageId }: { id: string; newDefaultStageId?: string }) =>
      unwrap(
        api.api.stages[":id"].$delete({
          param: { id },
          json: {
            confirmation: "DELETE ALL LEADS",
            ...(newDefaultStageId ? { newDefaultStageId } : {}),
          },
        }),
      ),
    onSuccess: async (result) => {
      await refresh();
      setDeleting(null);
      toast.success(
        result.deletedLeads
          ? `Stage deleted with ${result.deletedLeads} lead${result.deletedLeads === 1 ? "" : "s"}.`
          : "Stage deleted.",
      );
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const list = stages.data?.stages ?? [];
  const shift = (index: number, delta: -1 | 1) => {
    const stage = list[index]!;
    const without = list.filter((_, i) => i !== index);
    const target = index + delta;
    move.mutate({
      id: stage.id,
      beforeId: without[target - 1]?.id ?? null,
      afterId: without[target]?.id ?? null,
    });
  };

  return (
    <>
      <PageHeader
        title="Stages"
        description="The columns of your pipeline. New leads arrive in the default stage."
        actions={
          <Button
            onClick={() => {
              setFormError(undefined);
              setCreating(true);
            }}
          >
            <Plus /> New stage
          </Button>
        }
      />
      <Card padding="none" className="max-w-3xl overflow-hidden">
        {stages.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
        ) : list.length === 0 ? (
          <EmptyState icon={<Columns3 />} title="No stages" />
        ) : (
          <ul aria-label="Stages" className="flex flex-col divide-y divide-border">
            {list.map((stage, index) => (
              <li key={stage.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
                <div className="flex flex-col">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-6"
                    aria-label={`Move ${stage.name} earlier`}
                    disabled={index === 0 || move.isPending}
                    onClick={() => shift(index, -1)}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="size-6"
                    aria-label={`Move ${stage.name} later`}
                    disabled={index === list.length - 1 || move.isPending}
                    onClick={() => shift(index, 1)}
                  >
                    <ArrowDown />
                  </Button>
                </div>
                <StageDot color={stage.color as StageColor} className="size-3" />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 text-body font-medium text-fg">
                    {stage.name}
                    {stage.isDefault && <Badge tone="primary">Default</Badge>}
                  </p>
                  <p className="text-small text-fg-muted">
                    {stage.leadCount} lead{stage.leadCount === 1 ? "" : "s"}
                  </p>
                </div>
                <DefaultStageButton
                  stage={stage}
                  // Fill the heart straight away while the change saves.
                  isDefault={
                    update.isPending && update.variables?.isDefault
                      ? update.variables.id === stage.id
                      : stage.isDefault
                  }
                  onMakeDefault={() => update.mutate({ id: stage.id, isDefault: true })}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Edit ${stage.name}`}
                  onClick={() => {
                    setFormError(undefined);
                    setEditing(stage);
                  }}
                >
                  <Pencil />
                </Button>
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${stage.name}`}
                    onClick={() => setDeleting(stage)}
                  >
                    <Trash2 />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <StageFormDialog
        open={creating || Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
          }
        }}
        stage={editing ?? undefined}
        saving={create.isPending || update.isPending}
        error={formError}
        onSubmit={(values) =>
          editing ? update.mutate({ id: editing.id, ...values }) : create.mutate(values)
        }
      />
      <DeleteStageDialog
        key={deleting?.id}
        stage={deleting}
        stages={list}
        deleting={remove.isPending}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={(input) => deleting && remove.mutate({ id: deleting.id, ...input })}
      />
    </>
  );
}

/** A heart per stage: filled red on the default stage, click an empty one to make it the default. */
function DefaultStageButton({
  stage,
  isDefault,
  onMakeDefault,
}: {
  stage: { name: string };
  isDefault: boolean;
  onMakeDefault: () => void;
}) {
  return (
    <Tooltip
      content={
        isDefault ? "New leads arrive in this stage" : "Make this the stage new leads arrive in"
      }
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={
          isDefault ? `${stage.name} is the default stage` : `Make ${stage.name} the default stage`
        }
        aria-pressed={isDefault}
        aria-disabled={isDefault || undefined}
        className={cn(
          "transition-colors",
          isDefault
            ? "cursor-default text-error hover:bg-transparent hover:text-error"
            : "text-fg-muted hover:text-error",
        )}
        onClick={() => {
          if (!isDefault) onMakeDefault();
        }}
      >
        <Heart className={cn(isDefault && "fill-current")} />
      </Button>
    </Tooltip>
  );
}
